import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { CURRENCIES } from 'src/features/payStations/types';
import DetailsSection from 'src/features/reservations/components/DetailsSection';
import ReservationMetrics from 'src/features/reservations/components/ReservationMetrics';
import { useReservationTypeByVersionId } from 'src/features/reservations/hooks/useReservationTypeByVersionId';
import { useReservationTypeCurrentVersion } from 'src/features/reservations/hooks/useReservationTypeCurrentVersion';
import { RESERVATION_TYPE } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { GetPriceTaxAmounts, LatestScheduleChangesBadge } from 'src/features/reservations/utils/utils';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalMessages, generalPriceMessages } from 'src/utils/messages';

const DynamicReservationTypeDetails = () => {
    const { reservationType } = useParams();
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const [selectedVersionId, setSelectedVersionId] = React.useState<string>('');

    const { data: reservationTypeCurrentVersion } = useReservationTypeCurrentVersion({
        parkingId,
        reservationType: reservationType as RESERVATION_TYPE,
    });

    React.useEffect(() => {
        if (reservationTypeCurrentVersion) {
            setSelectedVersionId(reservationTypeCurrentVersion?.id);
        }
    }, [reservationType, reservationTypeCurrentVersion?.id]);

    const { data: reservationTypeDetails } = useReservationTypeByVersionId({
        parkingId,
        versionId: selectedVersionId,
    });

    const getEntryExitToleranceMin = (field: 'entry_tolerance_minutes' | 'exit_tolerance_minutes') => {
        if (!reservationTypeCurrentVersion) {
            return '-';
        }
        const fieldValue = reservationTypeDetails && reservationTypeDetails[field];
        return `${formatMessage(reservationMessages.minutes, { value: fieldValue })}`;
    };

    const reservationValidFrom = `${getFormattedDateTime(reservationTypeDetails?.valid_from).formattedDate} ${getFormattedDateTime(reservationTypeDetails?.valid_from).formattedTime}`;
    const reservationValidTo =
        reservationTypeDetails?.valid_to &&
        `${getFormattedDateTime(reservationTypeDetails?.valid_to).formattedDate} ${getFormattedDateTime(reservationTypeDetails?.valid_to).formattedTime}`;

    const currency: CURRENCIES | undefined = reservationTypeDetails?.currency;

    return (
        <>
            <ReservationMetrics selectedReservationType={reservationTypeDetails} />
            <div className="flex flex-col gap-y-4 md:grid md:grid-cols-1 md:gap-6 lg:grid-cols-2 lg:gap-6">
                <DetailsSection
                    title={formatMessage(reservationMessages.currentVersion)}
                    titleBadge={<LatestScheduleChangesBadge reservationTypeDetails={reservationTypeDetails} />}
                    data={[
                        {
                            title: formatMessage(reservationMessages.name),
                            value: reservationTypeDetails?.name,
                        },
                        {
                            title: formatMessage(reservationMessages.description),
                            value: reservationTypeDetails?.description,
                        },
                        {
                            title: formatMessage(reservationMessages.isActive),
                            value: reservationTypeDetails?.enabled
                                ? formatMessage(generalMessages.yes)
                                : formatMessage(generalMessages.no),
                        },
                        {
                            title: formatMessage(reservationMessages.reservationValidFrom),
                            value: reservationValidFrom,
                        },
                        {
                            title: formatMessage(reservationMessages.reservationValidTo),
                            value: reservationValidTo,
                        },
                    ]}
                />
                <DetailsSection
                    title={formatMessage(generalPriceMessages.pricing)}
                    data={[
                        {
                            title: formatMessage(generalPriceMessages.price),
                            value: GetPriceTaxAmounts(reservationTypeDetails?.price, currency),
                        },
                        {
                            title: `${formatMessage(generalPriceMessages.vat)} %`,
                            value:
                                reservationTypeDetails?.tax_percent && reservationTypeDetails?.price
                                    ? `${(parseFloat(reservationTypeDetails.tax_percent) * 100).toFixed(2)}%`
                                    : null,
                        },
                        {
                            title: formatMessage(generalPriceMessages.currency),
                            value: reservationTypeDetails?.price ? reservationTypeDetails?.currency : null,
                        },
                        {
                            title: formatMessage(generalPriceMessages.vatAmount),
                            value: GetPriceTaxAmounts(reservationTypeDetails?.tax_amount, currency),
                        },
                        {
                            title: formatMessage(generalPriceMessages.priceWithVat),
                            value: GetPriceTaxAmounts(reservationTypeDetails?.price_with_tax, currency),
                        },
                    ]}
                />
                <DetailsSection
                    title={formatMessage(reservationMessages.stayRules)}
                    data={[
                        {
                            title: formatMessage(reservationMessages.allowedSpots),
                            value: reservationTypeDetails?.allowed_spaces,
                        },
                        {
                            title: formatMessage(reservationMessages.entryTolerance),
                            value: getEntryExitToleranceMin('entry_tolerance_minutes'),
                        },
                        {
                            title: formatMessage(reservationMessages.exitTolerance),
                            value: getEntryExitToleranceMin('exit_tolerance_minutes'),
                        },
                        {
                            title: formatMessage(reservationMessages.reservedSpot),
                            value: reservationTypeDetails?.reserve_space
                                ? formatMessage(generalMessages.yes)
                                : formatMessage(generalMessages.no),
                        },
                    ]}
                />
            </div>
        </>
    );
};

export default DynamicReservationTypeDetails;
